import React, { FC, memo, useState, useCallback } from 'react';
import cn from 'clsx';

import StickyContainer from '../StickyContainer';
import ToggleSwitch from './ToggleSwitch';
import ShareDrawerContents from './ShareDrawerContents';
import CardFavoriteButton from '../CardsGrid/CardFavoriteButton';
import { CoinsStartingAt } from './PDPDetailsComponents';
import MobileCardCustomizeActions from './MobileCardCustomizeActions';
import MobileDrawer from '../Drawer/MobileDrawer';
import Share from '../Icons/Share';

import colors from '../../utils/colorConstants';
import isServer from '../../utils/isServer';
import { isCompositionPremium } from '../../utils/compositions';

interface Props {
  className?: string;
  id?: string;
  compositionId: string;
  name: string;
  comps?: any;
  partner: any;
  favorite?: boolean;
  shareImage: string;
  eventType: string;
  customizeTrackingProps: any;
  coinsStartingAt?: number;
  customizeURL: string;
  productType: string;
  toggleValue?: any;
  toggleCallback?: any;
  setCoinModalOpen: any;
}

const FREE_PRIMARY_OPTIONS = ['Free', 'Premium'];
const PREMIUM_PRIMARY_OPTIONS = ['Premium', 'Free'];

const MobileActionsBar: FC<Props> = (props) => {
  const {
    coinsStartingAt,
    compositionId,
    comps,
    customizeTrackingProps,
    customizeURL,
    eventType,
    favorite,
    id,
    name,
    partner,
    productType,
    setCoinModalOpen,
    shareImage,
    toggleCallback,
    toggleValue,
  } = props;
  const isReady = !!(id && eventType && customizeURL && typeof coinsStartingAt === 'number');
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const openShareDrawer = useCallback(() => setIsShareDrawerOpen(true), [setIsShareDrawerOpen]);
  const closeShareDrawer = useCallback(() => setIsShareDrawerOpen(false), [setIsShareDrawerOpen]);

  return (
    <StickyContainer
      className="lg:hidden overflow-auto shadow-md p-4"
      height="7.25rem"
      isReady={isReady}
    >
      <div className="flex flex-row justify-between pb-4">
        {productType === 'card' ? (
          <CoinsStartingAt coinsStartingAt={coinsStartingAt} questionClick={setCoinModalOpen} />
        ) : (
          <div className="flex items-center">
            {comps.length > 1 && (
              <ToggleSwitch
                options={
                  isCompositionPremium(comps[0]) ? PREMIUM_PRIMARY_OPTIONS : FREE_PRIMARY_OPTIONS
                }
                toggleCallback={toggleCallback}
                toggleValue={toggleValue}
              />
            )}
            <span
              className={cn('text-gatsby cursor-pointer paragraph-medium', {
                'ml-4': comps.length > 1,
              })}
              onClick={setCoinModalOpen}
            >
              Pricing
            </span>
          </div>
        )}
        <span className="flex items-center">
          {productType === 'card' && id && (
            <CardFavoriteButton className="p-0" favorite={favorite} id={id} stroke={colors.lapel} />
          )}
          <div className="pl-6" onClick={openShareDrawer} role="button">
            <Share />
          </div>
        </span>
      </div>
      {!isServer() && (
        <MobileCardCustomizeActions
          compositionId={compositionId}
          customizeTrackingProps={customizeTrackingProps}
          customizeURL={customizeURL}
          id={id}
          productType={productType}
        />
      )}
      {isShareDrawerOpen && (
        <MobileDrawer
          ariaLabel="Social sharing drawer"
          direction="bottom"
          isOpen={isShareDrawerOpen}
          toggleCallback={closeShareDrawer}
          tracking={{ id: 'pdpShareDrawer' }}
        >
          <ShareDrawerContents
            images={[shareImage]}
            name={name}
            onDismiss={closeShareDrawer}
            partner={partner}
            shareURL={`https://www.paperlesspost.com/cards/card/${id}`}
          />
        </MobileDrawer>
      )}
    </StickyContainer>
  );
};

export default memo(MobileActionsBar);
